import type React from "react";
import { useState } from "react";

import { faLockKeyhole } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { BaseButton } from "shared/atoms/BaseButton";
import { StyledInput } from "shared/atoms/inputs/StyledInputs";
import { Notification } from "shared/atoms/Notification";
import { WidgetBox } from "shared/containers/WidgetBox/WidgetBox";
import { theme } from "theme";

import { updatePassword } from "../../queries";
import { ButtonWrapper, EditFieldContainer, StyledTableRow, fieldDisplay, fieldWithBorder } from "../shared/styles";

const ProfilePasswordTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const PersonalPasswordValue = styled.div`
  ${fieldDisplay};
`;

const PersonalPasswordInput = styled(StyledInput)`
  ${fieldWithBorder};
`;

export const ProfilePassword: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const [isNotValid, setIsNotValid] = useState({ state: false, reason: "" });
  const { handleSubmit, register } = useForm<{
    current_password: string;
    new_password: string;
    confirm_new_password: string;
  }>();

  const { t } = useTranslation();

  const handleChangePasswordClick = () => {
    setIsEditing(!isEditing);
    setIsNotValid({ ...isNotValid, state: false, reason: "" });
  };

  const onSubmit = handleSubmit(async dataFromForm => {
    setIsNotValid({ ...isNotValid, state: false });
    const parcel = {
      current_password: dataFromForm.current_password,
      password: dataFromForm.new_password,
      password_confirmation: dataFromForm.confirm_new_password,
    };
    if (!dataFromForm.current_password) {
      setIsNotValid({ ...isNotValid, state: true, reason: "current_password_missing" });
      return;
    }
    if (dataFromForm.new_password !== dataFromForm.confirm_new_password) {
      setIsNotValid({ ...isNotValid, state: true, reason: "not_equal" });
      return;
    }
    if (dataFromForm.new_password.length <= 7) {
      setIsNotValid({ ...isNotValid, state: true, reason: "too_short" });
      return;
    }
    updatePassword(parcel)
      .then(() => {
        setIsEditing(!isEditing);
      })
      .catch(err => {
        setError(err);
      });
  });

  return (
    <WidgetBox
      icon={<FontAwesomeIcon icon={faLockKeyhole} />}
      title={t("settings.profile_password.change_profile_password")}
      color="blue"
      toolbar={
        !isEditing ? (
          <BaseButton
            onClick={handleChangePasswordClick}
            dataTestID="edit-profile-information-button"
            variant="action"
            icon="pen"
            text={t("settings.profile_information.change_profile_information")}
            size="large"
          />
        ) : undefined
      }
    >
      <form onSubmit={onSubmit} data-testid="profile-password">
        {isNotValid.state && (
          <Notification variant="danger" style={{ margin: "5px 0" }}>
            {/* FIXME: type translation */}
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            {t(`settings.profile_password.${isNotValid.reason}`)}
          </Notification>
        )}
        {error && (
          <Notification variant="danger" style={{ margin: "5px 0" }}>
            {t("errors.generic")}
          </Notification>
        )}

        <ProfilePasswordTable>
          {isEditing ? (
            <>
              <EditFieldContainer>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("settings.profile_password.password")}
                </Typography>
                <PersonalPasswordInput
                  data-testid="current-password"
                  {...register("current_password")}
                  type="password"
                  placeholder="************"
                />
              </EditFieldContainer>

              <EditFieldContainer>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("settings.profile_password.new_password")}
                </Typography>
                <PersonalPasswordInput
                  data-testid="new-password"
                  type="password"
                  {...register("new_password")}
                  placeholder=""
                />
              </EditFieldContainer>

              <EditFieldContainer>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("settings.profile_password.confirm_password")}
                </Typography>
                <PersonalPasswordInput
                  data-testid="confirm-password"
                  type="password"
                  {...register("confirm_new_password")}
                  placeholder=""
                />
              </EditFieldContainer>
            </>
          ) : (
            <StyledTableRow>
              <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                {t("settings.profile_password.password")}
              </Typography>
              <PersonalPasswordValue>************</PersonalPasswordValue>
            </StyledTableRow>
          )}
        </ProfilePasswordTable>
        {isEditing && (
          <ButtonWrapper>
            <BaseButton
              type="button"
              onClick={handleChangePasswordClick}
              text={t("buttons.cancel")}
              variant="secondary"
              size="large"
            />
            <BaseButton type="submit" text={t("buttons.save")} size="large" />
          </ButtonWrapper>
        )}
      </form>
    </WidgetBox>
  );
};
